import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { User, Building2 } from "lucide-react";
import Logo from "@/components/common/Logo";
import { Checkbox } from "@/components/ui/checkbox";
import { setNativeRolePreference } from "@/utils/nativeRoleStorage";

/**
 * Native app role selector screen - replaces landing pages for mobile users.
 * Presents a clean "Are you a Buyer or Seller?" choice with option to remember.
 */
const NativeRoleSelectorPage = () => {
  const navigate = useNavigate();
  const [rememberChoice, setRememberChoice] = useState(false);

  const handleBuyerClick = () => {
    if (rememberChoice) {
      setNativeRolePreference('buyer');
    }
    navigate("/buyer/login");
  };

  const handleSellerClick = () => {
    if (rememberChoice) {
      setNativeRolePreference('seller');
    }
    navigate("/login");
  };

  return (
    <div 
      className="min-h-screen bg-godyspo-background flex flex-col"
      style={{
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
        paddingLeft: "env(safe-area-inset-left)",
        paddingRight: "env(safe-area-inset-right)",
      }}
    >
      {/* Header with Logo */}
      <div className="flex justify-center pt-12 pb-8">
        <Logo size="large" disableLink />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6">
        {/* Welcome Text */}
        <div className="text-center mb-10">
          <h1 className="text-2xl font-bold text-foreground mb-2">
            Welcome to GoDyspo
          </h1>
          <p className="text-muted-foreground">
            How would you like to use the app?
          </p>
        </div>

        {/* Role Selection Cards */}
        <div className="w-full max-w-sm space-y-4">
          {/* Buyer Card */}
          <button
            onClick={handleBuyerClick}
            className="w-full p-6 rounded-xl border-2 border-godyspo-gold/30 bg-godyspo-gold/5 hover:bg-godyspo-gold/10 hover:border-godyspo-gold/50 transition-all duration-200 text-left group"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-godyspo-gold/20 flex items-center justify-center group-hover:bg-godyspo-gold/30 transition-colors">
                <User className="w-7 h-7 text-godyspo-gold" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-foreground mb-1">
                  I'm a Buyer
                </h2>
                <p className="text-sm text-muted-foreground">
                  Find investment properties
                </p>
              </div>
            </div>
          </button>

          {/* Seller Card */}
          <button
            onClick={handleSellerClick}
            className="w-full p-6 rounded-xl border-2 border-godyspo-blue/30 bg-godyspo-blue/5 hover:bg-godyspo-blue/10 hover:border-godyspo-blue/50 transition-all duration-200 text-left group"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-godyspo-blue/20 flex items-center justify-center group-hover:bg-godyspo-blue/30 transition-colors">
                <Building2 className="w-7 h-7 text-godyspo-blue" />
              </div>
              <div className="flex-1">
                <h2 className="text-lg font-semibold text-foreground mb-1">
                  I'm a Seller
                </h2>
                <p className="text-sm text-muted-foreground">
                  List your properties
                </p>
              </div>
            </div>
          </button>

          {/* Remember Choice Option */}
          <div className="flex flex-col items-center pt-4">
            <label 
              className="flex items-center gap-3 cursor-pointer"
              onClick={() => setRememberChoice(!rememberChoice)}
            >
              <Checkbox 
                checked={rememberChoice}
                onCheckedChange={(checked) => setRememberChoice(checked === true)}
              />
              <span className="text-sm text-muted-foreground">
                Remember my choice
              </span>
            </label>
            <p className="text-xs text-muted-foreground/60 mt-2">
              You can change this later in settings
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="py-6 text-center">
        <p className="text-xs text-muted-foreground">
          The #1 Real Estate Deal Marketplace
        </p>
      </div>
    </div>
  );
};

export default NativeRoleSelectorPage;
